/**
 * Hermite cúbica MONÓTONA (Fritsch–Carlson), pura e sem dependências.
 *
 * Existe por causa de um defeito medido na cinemática: a rolagem do veículo era
 * interpolada linearmente entre os quadros-chave, então a velocidade ANGULAR era
 * constante por trecho e saltava de um valor para outro em um único passo do
 * solver. No capotamento a rolagem parava seca em t=0,8, e o corpo dinâmico,
 * que não teleporta junto com o corpo cinemático, ganhava velocidade relativa do
 * nada — matéria-prima de impacto falso.
 *
 * Por que Hermite MONÓTONA e não Catmull-Rom: os ângulos da rolagem são as
 * paradas que a aula narra — 90° sobre a lateral, 180° invertido — e o
 * tombamento não pode passar de 90° em instante nenhum. Catmull-Rom passa pelos
 * nós mas ULTRAPASSA entre eles; a limitação de tangentes de Fritsch–Carlson
 * garante que uma sequência monótona produza uma curva monótona, sem excesso.
 *
 * E por que não a mesma redistribuição que a translação recebeu: ali há um perfil
 * de velocidade declarado e as posições eram o dado redundante. Aqui há só uma
 * taxa média declarada, e os ângulos são dado que carrega a lição — não dá para
 * derivá-los de outra coisa.
 */

/**
 * Tangentes limitadas pelo critério de Fritsch–Carlson.
 *
 * Exportada para ter teste próprio: é nela que mora a garantia de não ultrapassar,
 * e um erro aqui só apareceria como um veículo que gira 91° num tombamento de 90°.
 */
export function derivadasMonotonas(
  xs: readonly number[],
  ys: readonly number[],
): number[] {
  const n = xs.length
  if (n < 2) return new Array(n).fill(0)

  const h: number[] = []
  const delta: number[] = []
  for (let i = 0; i < n - 1; i += 1) {
    const dx = xs[i + 1]! - xs[i]!
    h.push(dx)
    delta.push(dx === 0 ? 0 : (ys[i + 1]! - ys[i]!) / dx)
  }

  const m: number[] = new Array(n).fill(0)
  m[0] = delta[0]!
  m[n - 1] = delta[n - 2]!
  for (let i = 1; i < n - 1; i += 1) {
    // patamar: um trecho plano de cada lado do nó tem de manter a curva plana,
    // senão a cauda parada da frontal e o platô de 180° do capotamento ganhariam
    // uma barriga que o aluno leria como o veículo andando depois de parar
    m[i] = delta[i - 1]! * delta[i]! <= 0 ? 0 : (delta[i - 1]! + delta[i]!) / 2
  }

  for (let i = 0; i < n - 1; i += 1) {
    if (delta[i] === 0) {
      m[i] = 0
      m[i + 1] = 0
      continue
    }
    const alfa = m[i]! / delta[i]!
    const beta = m[i + 1]! / delta[i]!
    const norma = Math.hypot(alfa, beta)
    // fora do círculo de raio 3 a cúbica deixa de ser monótona: encolhe as duas
    // tangentes para a borda dele
    if (norma > 3) {
      const fator = 3 / norma
      m[i] = fator * alfa * delta[i]!
      m[i + 1] = fator * beta * delta[i]!
    }
  }

  return m
}

export interface InterpoladorMonotono {
  /** valor da curva em `x`, limitado às extremidades */
  (x: number): number
  /** derivada dy/dx em `x` */
  derivada: (x: number) => number
}

/** Curva monótona por partes que passa exatamente por todos os nós. */
export function criarInterpoladorMonotono(
  xs: readonly number[],
  ys: readonly number[],
): InterpoladorMonotono {
  const n = xs.length
  const m = derivadasMonotonas(xs, ys)

  const trecho = (x: number) => {
    const limitado = Math.min(Math.max(x, xs[0]!), xs[n - 1]!)
    let i = 0
    for (let k = 0; k < n - 1; k += 1) {
      if (limitado >= xs[k]! && limitado <= xs[k + 1]!) {
        i = k
        break
      }
      i = k
    }
    const h = xs[i + 1]! - xs[i]!
    const s = h === 0 ? 0 : (limitado - xs[i]!) / h
    return { i, h, s }
  }

  const avaliar = (x: number): number => {
    if (n === 0) return 0
    if (n === 1) return ys[0]!
    const { i, h, s } = trecho(x)
    const s2 = s * s
    const s3 = s2 * s
    return (
      (2 * s3 - 3 * s2 + 1) * ys[i]! +
      (s3 - 2 * s2 + s) * h * m[i]! +
      (-2 * s3 + 3 * s2) * ys[i + 1]! +
      (s3 - s2) * h * m[i + 1]!
    )
  }

  const derivada = (x: number): number => {
    if (n < 2) return 0
    // fora do intervalo a curva é constante, e a derivada acompanha
    if (x < xs[0]! || x > xs[n - 1]!) return 0
    const { i, h, s } = trecho(x)
    if (h === 0) return 0
    const s2 = s * s
    return (
      ((6 * s2 - 6 * s) * ys[i]!) / h +
      (3 * s2 - 4 * s + 1) * m[i]! +
      ((-6 * s2 + 6 * s) * ys[i + 1]!) / h +
      (3 * s2 - 2 * s) * m[i + 1]!
    )
  }

  const interpolador = avaliar as InterpoladorMonotono
  interpolador.derivada = derivada
  return interpolador
}
